import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { FormGroup, ValidationErrors } from '@angular/forms'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import moment from 'moment'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { finalize, map } from 'rxjs/operators'
import { apis } from '../../environments/environment'
import { CustomerSite } from '../interfaces/customer-site'
import { FormValidationErrors } from '../interfaces/form-validation-errors'
import { Resp } from '../interfaces/response'
import { IAlertService } from '../libs/ialert/ialerts.service'
import { Customer } from '../models/customer'
import { DownloadLog } from '../models/download-log'
import { Permission } from '../models/permission'
import { Role } from '../models/role'
import { User } from '../models/user'
import { version } from './../../environments/environment'
import { releaseNotes } from './../../environments/environment.beta'
import identifiers from './component-identifiers'
import { ConstantsService } from './constants.service'
import { LovService } from './lov.service'
import { SessionStateService } from '../core/auth/session-state.service'

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    baseUrl: string

    rootUrl: string

    /**
     * Holds all the properties related to workshop/download-logs component
     */
    workshopDownloadLogs = {
        /**
         * Observable to publish list of download log. New items will be
         * pushed to this from RgGrid whenever a new download is made by
         * enduser.
         */
        list$: new BehaviorSubject<Array<DownloadLog>>([]),

        /**
         * Observable to publish collapsed statof list of download log.
         * List will be automatically Uncollapsed by RgGrid whenever a new
         * download is made by. This action will visually show that download
         * starts within that list.
         */
        listCollapsedStatus$: new BehaviorSubject<boolean>(true)
    }

    customerDownloadLogs = {
        /**
         * Observable to publish list of download log. New items will be
         * pushed to this from RgGrid whenever a new download is made by
         * enduser.
         */
        list$: new BehaviorSubject<Array<DownloadLog>>([]),

        /**
         * Observable to publish collapsed statof list of download log.
         * List will be automatically Uncollapsed by RgGrid whenever a new
         * download is made by. This action will visually show that download
         * starts within that list.
         */
        listCollapsedStatus$: new BehaviorSubject<boolean>(true)
    }

    /**
     * Used on Case Screen for applying for Next & Previous buttons
     */
    authWorkShopList: Array<unknown> = []


    scrollBottom: boolean

    scrollBottomChange = new Subject<boolean>()

    userImage = new Subject<string>()

    // Login state and the user live in SessionStateService; these are views onto it.
    get userLoggedInSource() {
        return this.session.userLoggedInSource
    }

    get userLoggedInObs() {
        return this.session.userLoggedIn$
    }

    get user(): User {
        return this.session.user
    }

    set user(value: User) {
        this.session.user = value
    }

    public version = version

    public releaseNotes = releaseNotes

    /**
     * Component Identifiers
     */
    compIdentifiers = identifiers

    /**
     * This property is specifically required for Driver
     * related screens. All driver screens need to know, on which
     * site driver is currently present.
     */
    customerSite: CustomerSite | null = null

    userChanged: Subject<User> = new Subject()


    lang: any

    /**
     * For common modules, logged-in customer details can be set & fetched
     * from this property
     */
    workshopSelectedCustomer: Customer | null = null

    constructor(
        public http: HttpClient,
        public cs: ConstantsService,
        private lov: LovService,
        private ts: TranslateService,
        private router: Router,
        private alert: IAlertService,
        private session: SessionStateService
    ) {
        this.baseUrl = apis.baseUrl + '/public'
        // Any proxy prefix (e.g. /backend) is whatever precedes /api/vN, as in RgHttpService.getCsrfCookie()
        this.rootUrl = apis.baseUrl.replace(/\/api\/v\d+\/?$/, '')

        this.scrollBottom = false
        this.scrollBottomChange.subscribe((value) => {
            this.scrollBottom = value
        })

        if (this.user && this.user.userable_type == 'workshop') {
            this.getMyWorkShopList()
        }

        this.lang = this.translate('common')
        this.lang.subscribe((d: any) => {
            this.lang = d
        })
    }

    toggleScrollBottom(value: boolean): void {
        this.scrollBottomChange.next(value)
    }

    /**
     * Fetch all workshops of the auth user
     */
    getMyWorkShopList() {
        const url = `${apis.baseUrl}/workshop/auth/my-workshops`

        this.http
            .get<Resp<Array<IMyWorkshop>>>(url)
            .subscribe((resp: Resp<Array<IMyWorkshop>>) => {
                this.authWorkShopList = resp.data
                this.authWorkShopList.unshift({
                    id: 0,
                    name: 'All Workshops',
                    primary: false
                })
            })
    }

    setCookie(): Observable<any> {
        const url = `${this.rootUrl}/sanctum/csrf-cookie`

        return this.http.get<void>(url, {
            withCredentials: true
        })
    }

    login(params: any): Observable<any> {
        const url = `${this.baseUrl}/login`

        return this.http.post<any>(url, params).pipe(
            map((resp) => {
                if (resp && resp.success) {
                    this.session.setUser(resp.data)
                    if (this.user && this.user.userable_type == 'workshop') {
                        this.getMyWorkShopList()
                    }
                }

                return resp
            })
        )
    }

    googleLogin(): Observable<any> {
        const url = `${this.baseUrl}/login/${'google'}`

        return this.http.get<any>(url).pipe(
            map((resp) => {
                if (resp && resp.success) {
                    this.session.setUser(resp.data)
                }

                return resp
            })
        )
    }

    register(params: any): Observable<any> {
        const url = `${this.baseUrl}/register`

        return this.http.post<any>(url, params).pipe(
            map((resp) => {
                if (resp && resp.success) {
                    this.session.setUser(resp.data)
                }

                return resp
            })
        )
    }

    doUserRedirects(resp: any, router: Router) {
        // router.navigate(['/user/dashboard'])
        switch (resp.data.userable_type) {
            case ConstantsService.USER_ROLES.ADMIN: {
                router.navigate(['/admin/dashboard'])
                break
            }
            case ConstantsService.USER_ROLES.WORKSHOP: {
                router.navigate(['/workshop/dashboard'])
                break
            }
            case ConstantsService.USER_ROLES.CUSTOMER: {
                router.navigate(['/customer/dashboard'])
                break
            }
        }
    }

    /** `remote`: another tab ended the session, so only reset this tab (see SessionStateService.logOut). */
    logOut(opts: { remote?: boolean } = {}): boolean {
        this.session.logOut(opts)
        return true
    }

    /** Record a sign-in that happened outside `login()` (social login, verification links). */
    startSession(user: any): void {
        this.session.setUser(user)
    }

    logOutSession(): Observable<any> {
        const url = `${this.baseUrl}/logout`

        return this.http.post<any>(url, {
            fcm_token: this.user?.fcm_token ?? null
        })
    }

    getAuthStateVersion(): number {
        return this.session.getAuthStateVersion()
    }

    logOutAndRedirect(
        redirectUrl: string = '/login',
        reloadPage: boolean = false
    ): void {
        this.logOutSession()
            .pipe(
                finalize(() => {
                    this.logOut()

                    if (reloadPage) {
                        window.location.reload()
                        return
                    }

                    window.location.href = redirectUrl
                })
            )
            .subscribe({
                next: () => {},
                error: () => {}
            })
    }

    /**
     * Checks if user holds any of the permission from the provided
     * list of permissions.
     *
     * @param permissions Array Permission names
     * @returns boolean
     */
    checkPermissions(permissions: Array<string>): boolean {
        if (!Array.isArray(permissions) || permissions.length === 0) {
            return true
        }

        if (!this.user) {
            this.router.navigateByUrl('/login')
            return false
        }

        if (this.user.userable_type === ConstantsService.USER_ROLES.ADMIN) {
            return true
        }

        let permissionFound = false
        permissions.map((permission: any) => {
            if (this.checkPermission(permission)) {
                permissionFound = true

                return
            }
        })

        return permissionFound
    }

    /**
     * Check if the logged in user holds the provided permission or not.
     *
     * It checks for all permissions
     *
     * @param permission string Permission Name
     * @returns boolean
     */
    checkPermission(permission: string): boolean {
        if (!this.user || !permission) {
            return false
        }

        // return if user type is admin
        if (this.user.userable_type === ConstantsService.USER_ROLES.ADMIN) {
            return true
        }

        // return if user has role admin
        if (!Array.isArray(this.user.roles)) {
            return false
        }

        const checkUrserRole = this.user.roles.findIndex(
            (r) => r.name === ConstantsService.USER_ROLES.ADMIN
        )
        if (checkUrserRole > -1) {
            return true
        }

        // return if user has direct permission
        if (!Array.isArray(this.user.permissions)) {
            return false
        }

        const index = this.user.permissions.findIndex(
            (item: any) => item.name === permission
        )
        if (index > -1) {
            return true
        }

        // return if user has permission through current/default role
        if (!this.user.current_role || !this.user.current_role.id) {
            return false
        }

        const currentRolePermissions = this.getRolePermissions(
            this.user.current_role.id
        )
        if (!currentRolePermissions) {
            return false
        }

        const i: number = currentRolePermissions.findIndex(
            (item: any) => item.name === permission
        )
        if (i > -1) {
            return true
        }

        return false
    }

    /**
     * Check if the logged in user hold the provided role or not
     * @returns boolean
     */
    checkAllRoles(role: string): boolean {
        if (!this.user || !this.user.roles) {
            return false
        }

        const checkUrserRole = this.user.roles.findIndex((r) => r.name === role)
        if (checkUrserRole > -1) {
            return true
        } else {
            return false
        }
    }

    isAuthenticated(): boolean {
        if (localStorage.getItem('user')) {
            return true
        }

        return false
    }

    isCustomer(): boolean {
        if (
            this.isAuthenticated() &&
            this.user.userable_type === ConstantsService.USER_ROLES.CUSTOMER
        ) {
            return true
        }

        return false
    }

    isWorkshop(): boolean {
        if (
            this.isAuthenticated() &&
            this.user.userable_type === ConstantsService.USER_ROLES.WORKSHOP
        ) {
            return true
        }

        return false
    }

    isDriver(): boolean {
        if (
            this.isAuthenticated() /** && check if user has Driver role or not */
        ) {
            return true
        }

        return false
    }

    isAdmin(): boolean {
        if (
            this.isAuthenticated() &&
            this.user.userable_type === ConstantsService.USER_ROLES.ADMIN
        ) {
            return true
        }

        return false
    }

    jsonToFormData(
        jsonObject: any,
        parentKey?: any,
        carryFormData?: FormData
    ): FormData {
        const formData = carryFormData || new FormData()
        let index = 0

        // tslint:disable-next-line: forin
        for (const key in jsonObject) {
            if (jsonObject.hasOwnProperty(key)) {
                if (jsonObject[key] !== null && jsonObject[key] !== undefined) {
                    let propName = parentKey || key
                    if (parentKey && this.isObject(jsonObject)) {
                        propName = parentKey + '[' + key + ']'
                    }
                    if (parentKey && this.isArray(jsonObject)) {
                        propName = parentKey + '[' + index + ']'
                    }
                    if (jsonObject[key] instanceof File) {
                        formData.append(propName, jsonObject[key])
                    } else if (jsonObject[key] instanceof FileList) {
                        for (let j = 0; j < jsonObject[key].length; j++) {
                            formData.append(
                                propName + '[' + j + ']',
                                jsonObject[key].item(j)!
                            )
                        }
                    } else if (
                        this.isArray(jsonObject[key]) ||
                        this.isObject(jsonObject[key])
                    ) {
                        this.jsonToFormData(jsonObject[key], propName, formData)
                    } else if (typeof jsonObject[key] === 'boolean') {
                        formData.append(propName, +jsonObject[key] ? '1' : '0')
                    } else {
                        formData.append(propName, jsonObject[key])
                    }
                }
            }
            index++
        }

        return formData
    }

    getFormValidationErrors(form: FormGroup): FormValidationErrors {
        const response: FormValidationErrors = {
            totalErrors: 0,
            general: '',
            list: []
        }

        const errorMessages: any = {
            required: (fieldName: string, params: any) =>
                `${fieldName} is required.`,
            maxlength: (fieldName: string, params: any) =>
                `${fieldName} must not exceeds ${params.requiredLength} characters.`,
            minlength: (fieldName: string, params: any) =>
                `${fieldName} must have at least ${params.requiredLength} characters.`,
            pattern: (fieldName: string, params: any) =>
                `Invalid format for ${fieldName}`,
            min: (fieldName: string, params: any) =>
                `${fieldName} shouldn't be any number below ${params.min}`,
            max: (fieldName: string, params: any) =>
                `${fieldName} value shouldn't exceeds ${params.max}`,
            whitespace: (fieldName: string, params: any) =>
                `White spaces are not allowed in ${fieldName}`
        }

        Object.keys(form.controls).forEach((fieldName) => {
            const controlErrors: ValidationErrors | null =
                form!.get(fieldName)!.errors
            if (controlErrors != null) {
                response.totalErrors++
                Object.keys(controlErrors).forEach((errorType) => {
                    const field = fieldName.split('_')
                    field.forEach((f: string, i: number) => {
                        field[i] = this.snakeToCamel(f)
                    })
                    fieldName = field.join(' ')
                    response.list.push(
                        errorMessages[errorType](
                            fieldName,
                            controlErrors[errorType]
                        )
                    )
                    response.general = errorMessages[errorType](
                        fieldName,
                        controlErrors[errorType]
                    )
                })
            }
        })

        return response
    }

    snakeToCamel(str: string) {
        return str
            .replace(/_([a-z])/g, function (match, letter) {
                return letter.toUpperCase()
            })
            .replace(/^./, function (match) {
                return match.toUpperCase()
            })
    }

    isArray(val: any) {
        const toString = {}.toString

        return toString.call(val) === '[object Array]'
    }

    isObject(val: any) {
        return !this.isArray(val) && typeof val === 'object' && !!val
    }

    checkVerificationCode(data: any): Observable<any> {
        const url = `${this.baseUrl}/verify-email`

        return this.http.post<any>(url, data)
    }

    resendVerificationCode(data: any): Observable<any> {
        const url = `${this.baseUrl}/resend-code`

        return this.http.post<any>(url, data)
    }

    checkResetCode(data: any): Observable<any> {
        const url = `${this.baseUrl}/verify-code`

        return this.http.post<any>(url, data)
    }

    resetPass(data: any): Observable<any> {
        const url = `${this.baseUrl}/reset-password`

        return this.http.post<any>(url, data)
    }

    translate(key: string) {
        return this.ts.get(key)
    }

    getCurrentLang() {
        let lang = localStorage.getItem('lang')
            ? localStorage.getItem('lang')
            : this.ts.getBrowserLang()
        if (!lang) {
            lang = 'en'
        }
        return lang
    }

    changeLanguage(lang: string) {
        localStorage.setItem('lang', lang)
        this.ts.use(lang)
        location.reload()
    }

    userImageUrl(userId?: number) {
        userId = userId ? userId : -1

        return `${apis.baseUrl}/public/profile-image/${userId}`
    }

    deepClone(obj: any) {
        if (obj == null || typeof obj !== 'object') {
            return obj
        }

        if (obj instanceof Date) {
            return new Date(obj)
        }
        if (obj instanceof String) {
            return new String(obj)
        }
        if (obj instanceof Number) {
            return new Number(obj)
        }
        if (obj instanceof Boolean) {
            return new Boolean(obj)
        }
        if (obj instanceof RegExp) {
            return new RegExp(obj)
        }

        // handle other objects if required
        let clone: any = {}
        if (obj instanceof Array) {
            clone = new Array(obj.length)
        }

        Object.keys(obj).map((key: any) => {
            clone[key] = this.deepClone(obj[key])
        })

        return clone
    }

    /**
     * Check if assetId enabled or not
     * @returns boolean
     */
    isAssetIdEnabled(cust: Customer | null = null): boolean {
        const customer = this.getCustomer(cust)
        return customer === false ? false : customer.enable_asset_id
    }

    /**
     * Deprecated. Should be removed
     * @param cust
     * @returns
     */
    isDropoffLocationEnabled(cust: Customer | null = null): boolean {
        return false
    }

    isHomeLocationEnabled(cust: Customer | null = null): boolean {
        const customer = this.getCustomer(cust)
        return customer === false ? false : customer.enable_home_location
    }

    isAssetDiscoveryPromptEnabled(cust: Customer | null = null): boolean {
        const customer = this.getCustomer(cust)
        return customer === false ? false : customer.asset_discovery_prompt
    }

    iswoAclGlobalAccessEnabled(cust: Customer | null = null): boolean {
        const customer = this.getCustomer(cust)
        return customer === false ? false : !customer.enable_wo_acl_gam
    }

    isRequestIdEnabled(cust: Customer | null = null): boolean {
        const customer = this.getCustomer(cust)
        return customer === false ? false : customer.enable_request_id
    }

    isSamePickupAndDropoffLocationsEnabled(
        cust: Customer | null = null
    ): boolean {
        const customer = this.getCustomer(cust)
        return customer === false
            ? false
            : customer.enable_same_pickup_and_dropoff_location
    }

    isFixedPickupLocationEnabled(cust: Customer | null = null): boolean {
        const customer = this.getCustomer(cust)
        return customer === false
            ? false
            : customer.enable_fixed_pickup_location
    }

    isShippingEnabled(cust: Customer | null = null): boolean {
        const customer = this.getCustomer(cust)
        return customer === false ? false : customer.enable_shipping
    }

    isFixedDropoffLocationEnabled(cust: Customer | null = null): boolean {
        const customer = this.getCustomer(cust)
        return customer === false
            ? false
            : customer.enable_fixed_dropoff_location
    }

    isNowClaimDecisionClubbingEnabled(cust: Customer | null = null): boolean {
        const customer = this.getCustomer(cust)
        return customer === false
            ? false
            : customer.enable_now_claim_decision_clubbing
    }

    isWorkOrderBlackListEnabled(cust: Customer | null = null): boolean {
        const customer = this.getCustomer(cust)
        return customer === false
            ? false
            : customer.enable_work_order_black_list
    }

    isWorkOrderWhiteListEnabled(cust: Customer | null = null): boolean {
        const customer = this.getCustomer(cust)
        return customer === false
            ? false
            : customer.enable_work_order_white_list
    }

    getCustomer(cust: Customer | null = null) {
        if (this.isCustomer()) {
            return this.user.userable as Customer
        }

        if (cust != null) {
            return cust
        }

        if (this.workshopSelectedCustomer != null) {
            return this.workshopSelectedCustomer
        }

        return false
    }

    /**
     * This method is only applicable for driver's panel.
     * When driver chooses a site for pickup or dropoff, details of
     * the site are stored in Local Storage. This method will retrieve
     * and return those details.
     *
     * @returns CustomerSite
     */
    getCustomerSite(): CustomerSite | null {
        const data = localStorage.getItem('customer-site-data')
        if (!data) {
            return null
        }

        return JSON.parse(data) as CustomerSite
    }

    caseNo(id: number): string {
        return `${this.lang.prefix}-${id.toString().padStart(4, '0')}`
    }

    tileNo(id: number, prefix?: string): string {
        if (prefix) {
            return `${prefix}-${id.toString().padStart(2, '0')}`
        } else {
            return id.toString().padStart(2, '0')
        }
    }

    getKeyByValue(object: any, value: string): string | undefined {
        return Object.keys(object).find((key) => object[key] === value)
    }

    dateDiff(startDate: any, endDate?: any, format?: 'miliseconds') {
        const end = endDate == undefined ? moment(new Date()) : moment(endDate)
        const start = moment(startDate)
        const duration = moment.duration(start.diff(end))

        if (format == 'miliseconds') {
            return duration.asMilliseconds()
        }

        return duration.humanize()
    }

    dateDiffMilli(startDate: any, endDate?: any) {
        this.dateDiff(startDate, endDate, 'miliseconds')
    }

    public playSound(name: string, ext: string = ''): void {
        // notification, ping
        const audio = new Audio()
        ext = !ext ? 'mp3' : ext
        audio.src = `assets/audios/${name}.${ext}`
        audio.load()
        audio.play()
    }

    /**
     * Copies text to clipboard
     */
    copyToClipboard(text: string): void {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                this.alert.success('Copied to clipboard!')
            })
            .catch((err) => {
                this.alert.error('Failed to copy')
            })
    }

    changeRole(roleId: number) {
        if (this.user.current_role.id == roleId) {
            return
        }

        const role = this.user.roles.findById(roleId)
        if (role) {
            this.user.current_role = this.deepClone(role)
            this.session.updateUser(this.user)

            /*
            if (this.user.current_role.home_screen_url) {
                const url = new URL(this.user.current_role.home_screen_url)
                const internalPath = url.pathname
                // if internalPath is equal to current path, then reload the page
                if (this.router.url === internalPath) {
                    window.location.href = this.user.current_role.home_screen_url
                }
                else {
                    this.router.navigateByUrl(internalPath)
                }
            }
            else {
                this.router.navigate(['/', this.user.userable_type])
            }
            */
            this.router.navigate([
                '/',
                this.user.userable_type,
                'role-switched'
            ])
            this.alert.success(this.lang.role_changed)
            this.userChanged.next(this.user)
        }
    }

    syncUserRolesAndPermission() {
        const url = `${apis.baseUrl}/${this.user.userable_type}/auth/user-data`

        this.http.get<any>(url).subscribe((resp) => {
            if (resp && resp.success) {
                const oldUser = JSON.parse(localStorage.getItem('user') as any)
                const newUser = resp.data
                // console.log(this.hasRoleOrPermissionChanged(oldUser, newUser))

                if (this.hasRoleOrPermissionChanged(oldUser, newUser)) {
                    this.session.updateUser(resp.data)
                    this.router.navigate(['/', this.user.userable_type])
                    this.alert.info('Roles and permission changes successfull.')
                    this.userChanged.next(this.user)
                    this.changeRole(newUser.current_role.id)
                }
            }

            return resp
        })
    }

    getRolePermissions(roleId: number) {
        const role = this.user.roles.findById(roleId)
        if (role) {
            return this.deepClone(role.permissions)
        } else {
            return []
        }
    }

    hasDriverPerimission(): boolean {
        if (!this.user || !Array.isArray(this.user.permissions)) {
            return false
        }

        // check for direct permission
        const index = this.user.permissions.findIndex(
            (item: Permission) => item.permission_sub_type == 'driver'
        )
        if (index > -1) {
            return true
        }

        // return if user has permission through current/default role
        if (!this.user.current_role || !this.user.current_role.id) {
            return false
        }

        const currentRolePermissions = this.getRolePermissions(
            this.user.current_role.id
        )
        const i = currentRolePermissions.findIndex(
            (item: Permission) => item.permission_sub_type == 'driver'
        )
        if (i > -1) {
            return true
        }

        return false
    }

    /**
     * This method will iterate through all the active roles
     * of logged-in user and return the highest role level
     * among all the roles.
     *
     * @returns number The Highest Role Level of this User
     */
    getMaxRoleLevel(): number {
        let maxRoleLevel = 0
        this.user.roles.map((role: Role) => {
            if (role.level > maxRoleLevel) {
                maxRoleLevel = role.level
            }
        })

        return maxRoleLevel
    }

    normalizePermissions(perms: any[]) {
        return perms
            .map(({ pivot, ...rest }) => rest) // ignore pivot
            .sort((a, b) => a.id - b.id)
    }

    hasRoleOrPermissionChanged(oldUser: any, newUser: any): boolean {
        const oldRoles = (oldUser.roles || []).sort(
            (a: any, b: any) => a.id - b.id
        )
        const newRoles = (newUser.roles || []).sort(
            (a: any, b: any) => a.id - b.id
        )

        // Role count changed
        if (oldRoles.length !== newRoles.length) {
            return true
        }

        for (let i = 0; i < oldRoles.length; i++) {
            const oldRole = oldRoles[i]
            const newRole = newRoles[i]

            // Role identity changed
            if (oldRole.id !== newRole.id) {
                return true
            }

            const oldPerms = this.normalizePermissions(
                oldRole.permissions || []
            )
            const newPerms = this.normalizePermissions(
                newRole.permissions || []
            )

            // Permission count changed
            if (oldPerms.length !== newPerms.length) {
                return true
            }

            //  Permission content changed
            for (let j = 0; j < oldPerms.length; j++) {
                if (
                    JSON.stringify(oldPerms[j]) !== JSON.stringify(newPerms[j])
                ) {
                    return true
                }
            }
        }
        const oldPerms = this.normalizePermissions(oldUser.permissions || [])
        const newPerms = this.normalizePermissions(newUser.permissions || [])

        // Individual Permission count changed
        if (oldPerms.length !== newPerms.length) {
            return true
        }

        // Individual Permission content changed
        for (let j = 0; j < oldPerms.length; j++) {
            if (JSON.stringify(oldPerms[j]) !== JSON.stringify(newPerms[j])) {
                return true
            }
        }
        if (oldUser.current_role.id != newUser.current_role.id) return true
        return false // ✅ no change
    }

    /**
     * There is some data which being store in browser's Local Storage
     * and in ApiService. It's being filled at the time of user's login only.
     *
     * This method can be called anytime to sysc that data manually.
     */
    syncUserData() {
        const url = `${apis.baseUrl}/${this.user.userable_type}/auth/user-data`

        return this.http.get<any>(url).pipe(
            map((resp) => {
                if (this.user && this.user.current_role) {
                    resp.data.current_role = this.user.current_role
                }

                if (resp && resp.success) {
                    this.session.updateUser(resp.data)
                }

                return resp
            })
        )
    }

    /**
     * This method will udpate user token in the database
     * @param token
     * @returns
     */
    updateUserFcmToken(token: string) {
        const url = `${apis.baseUrl}/${this.user.userable_type}/fcm/register-token`

        return this.http.post<any>(url, { fcm_token: token })
    }

    /**
     * Utility method. Will return keys of the input object.
     */
    getObjectKeys(o: object) {
        return Object.keys(o)
    }

    /**
     * This method will publish notification for user in the browser
     * with a beep sound.
     *
     * @param title
     * @param msg
     * @param options
     */
    publishNotification(title: string, msg: string, options = null) {
        this.alert.info(msg, title, {
            interval: 25000,
            taIdentifier: 'notification-alert'
        })
        const tone: string = this.user.notification_tone
            ? this.user.notification_tone
            : 'default'
        this.playSound(`notifications/${tone}`)
    }

    removeUndefinedKeys<T extends object>(obj: T): Partial<T> {
        return Object.fromEntries(
            Object.entries(obj).filter(
                ([_, value]) => value !== undefined && value != 'undefined'
            )
        ) as Partial<T>
    }

    /**
     * Downlaod latest release version of application
     */
    downloadReleaseDoc(version: string) {
        const pdfUrl = `assets/release-docs/Release-${version}.pdf`

        // Open the PDF in a new tab
        window.open(pdfUrl, '_blank')
    }

    playSuccess(): void {
        this.playSound('success')
    }

    playError(): void {
        this.playSound('error')
    }

    playWarning(): void {
        this.playSound('warning')
    }
}

export interface IMyWorkshop {
    id: number
    name: string
    primary: boolean
}
